import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBillingStatus,
  getBillingInvoices,
  createPortalSession,
  createMigrationCheckout,
  verifyCheckoutSession,
  getPlanPrices,
  createAddSubscriptionCheckout,
  reactivateSubscription,
  purchaseDedicatedIp,
  getDedicatedIpPrice,
  getUpgradeOptions,
  previewUpgradePlan,
  upgradePlan,
  cancelUpgradeRequest,
  previewIntervalSwitch,
  switchToAnnual,
} from '../api/billing';
import type { AddSubscriptionRequest, UpgradePlanRequest } from '../types';

/**
 * Hook to get billing status for current user's organization
 */
export function useBillingStatus() {
  return useQuery({
    queryKey: ['billing', 'status'],
    queryFn: getBillingStatus,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to verify a checkout session after redirect
 */
export function useCheckoutSession(sessionId: string | null) {
  return useQuery({
    queryKey: ['billing', 'checkout', sessionId],
    queryFn: () => verifyCheckoutSession(sessionId!),
    enabled: !!sessionId,
    retry: 3,
    retryDelay: 1000,
  });
}

/**
 * Hook to create a Stripe portal session and redirect
 */
export function usePortalSession() {
  return useMutation({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      // Redirect to Stripe portal
      window.location.href = data.url;
    },
  });
}

/**
 * Hook to create a migration checkout session and redirect
 */
export function useMigrationCheckout() {
  return useMutation({
    mutationFn: createMigrationCheckout,
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    },
  });
}

/**
 * Hook to get available plan prices for adding a subscription
 */
export function usePlanPrices() {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: getPlanPrices,
    staleTime: 30 * 60 * 1000, // 30 minutes - prices don't change often
  });
}

/**
 * Hook to create an add subscription checkout session and redirect
 */
export function useAddSubscriptionCheckout() {
  return useMutation({
    mutationFn: (request: AddSubscriptionRequest) =>
      createAddSubscriptionCheckout(request),
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    },
  });
}

/**
 * Hook to reactivate a suspended/archived subscription via Stripe checkout
 */
export function useReactivateSubscription() {
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      reactivateSubscription(subscriptionId),
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    },
  });
}

/**
 * Hook to purchase a dedicated IP add-on for a subscription
 */
export function useDedicatedIpPurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, confirmDueToday }: { subscriptionId: string; confirmDueToday?: number }) =>
      purchaseDedicatedIp(subscriptionId, confirmDueToday),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

/**
 * Hook to get the dedicated IP add-on price for a subscription.
 * Fetches only while the purchase modal is open (enabled).
 */
export function useDedicatedIpPrice(subscriptionId: string, enabled = true) {
  return useQuery({
    queryKey: ['billing', 'dedicated-ip-price', subscriptionId],
    queryFn: () => getDedicatedIpPrice(subscriptionId),
    enabled,
    retry: false, // informational only — the modal falls back to price-less wording
    staleTime: 5 * 60 * 1000, // price changes rarely; avoid refetch on every modal open
  });
}

/**
 * Hook to get per-subscription plan upgrade options
 */
export function useUpgradeOptions() {
  return useQuery({
    queryKey: ['billing', 'upgrade-options'],
    queryFn: getUpgradeOptions,
    staleTime: 60 * 1000, // 1 minute — must refresh promptly after upgrades/requests
  });
}

/**
 * Hook to preview the prorated charge for a stripe_instant upgrade.
 * Fetches as soon as a plan is selected (planName non-null) and enabled is true.
 */
export function useUpgradePreview(
  subscriptionId: string,
  planName: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: ['billing', 'upgrade-preview', subscriptionId, planName],
    queryFn: () =>
      previewUpgradePlan({
        subscription_id: subscriptionId,
        plan_name: planName!,
      }),
    enabled: enabled && !!planName,
    staleTime: 60 * 1000,
    // Keep an open modal's quoted charge current (proration drifts as the
    // renewal date approaches / passes)
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook to execute a plan upgrade.
 * legacy_checkout → redirect to Stripe Checkout (same UX as migrate/add-subscription).
 * stripe_instant / request → invalidate subscriptions + billing queries so the
 * new plan / pending-request badge appear.
 */
export function useUpgradePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpgradePlanRequest) => upgradePlan(request),
    onSuccess: (data) => {
      if (data.flow === 'legacy_checkout') {
        // Redirect to Stripe checkout; verification lands on /billing/success
        window.location.href = data.checkout_url;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

/**
 * Hook to preview the charge for switching a subscription from monthly to
 * annual billing. Fetches only while the confirmation modal is open.
 *
 * `enabled` is required (no default): unlike `useUpgradePreview`, which has
 * a natural second gate on `!!planName`, subscriptionId is always known at
 * render time here — a caller that forgot to pass `enabled={isOpen}` would
 * fire the Stripe upcoming-invoice POST on mount and re-poll it every 60s
 * indefinitely.
 */
export function useIntervalSwitchPreview(subscriptionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['billing', 'interval-switch-preview', subscriptionId],
    queryFn: () => previewIntervalSwitch(subscriptionId),
    enabled: enabled && !!subscriptionId,
    staleTime: 60 * 1000,
    // Keep an open modal's quoted charge current (proration drifts as the
    // renewal date approaches / passes)
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    // An ineligible sub (400) won't become eligible on retry — matches the
    // useDedicatedIpPrice donor's posture for informational preview calls
    retry: false,
  });
}

/**
 * Hook to switch a subscription from monthly to annual billing.
 * Charges immediately — invalidates subscriptions + billing queries so the
 * new frequency / renewal date appear.
 *
 * IMPORTANT for consumers: `['billing']` is a prefix of the interval-switch
 * preview query key, so this invalidation refetches any *still-enabled*
 * preview for the now-annual subscription straight into a 400 (not_eligible)
 * error state — invalidation only marks disabled queries stale, it does not
 * skip a mounted, enabled query.
 *
 * This invalidation happens inside this hook's own `onSuccess`, which
 * `mutation.execute()` awaits *before* dispatching the mutation's 'success'
 * state — i.e. before React has rendered `isSuccess: true`, and sometimes
 * before it has ever rendered an intermediate `isPending: true` frame either
 * (verified: with a fast/mocked mutationFn, the pending→success dispatch
 * pair can land inside one microtask burst that React coalesces into a
 * single render). So a flag flipped from `onSuccess`/`onError` on `.mutate()`
 * — or one merely *derived* from `isPending`/`isSuccess` — is not reliable:
 * it can still read `enabled: true` at the exact instant this hook's
 * `invalidateQueries` decides which queries are active.
 *
 * The modal MUST instead disable the preview query *synchronously*, before
 * ever calling `.mutate()` — e.g. `flushSync(() => setChargeInFlight(true))`
 * immediately before `switchToAnnual.mutate(...)` — so the query observer's
 * `enabled` option is already `false` by the time this invalidation runs, no
 * matter how fast the mutation resolves. Re-enable in `.mutate()`'s
 * call-level `onError` so a failed charge (e.g. a card decline) still allows
 * retry with a live quote. See `IntervalSwitchModal.tsx` for the reference
 * implementation (the `chargeInFlight` state + the `flushSync` call in
 * `handleConfirm`).
 */
export function useSwitchToAnnual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) => switchToAnnual(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

/**
 * Hook to cancel a pending plan upgrade request
 */
export function useCancelUpgradeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => cancelUpgradeRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['billing', 'upgrade-options'],
      });
    },
  });
}

/**
 * Hook to list the invoices of an invoice-billed organization
 * Call only below the canAccessBilling gate (the server returns 403 for tech users).
 */
export function useBillingInvoices() {
  return useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: getBillingInvoices,
    staleTime: 5 * 60 * 1000,
  });
}
